import { ALL_CANDIDATES, bit, lowestDigit, popcount, type Grid } from './types';
import { BOX_OF, COL_OF, PEERS, ROW_OF } from './units';
import { shuffle, type Rng } from './rng';

/**
 * Backtracking-Löser mit Bitmasken und MRV-Heuristik
 * (Minimum Remaining Values: immer die Zelle mit den wenigsten Kandidaten zuerst).
 */

interface Masks {
  rows: Int32Array;
  cols: Int32Array;
  boxes: Int32Array;
}

function buildMasks(grid: Grid): Masks | null {
  const rows = new Int32Array(9);
  const cols = new Int32Array(9);
  const boxes = new Int32Array(9);
  for (let i = 0; i < 81; i++) {
    const v = grid[i];
    if (v === 0) continue;
    const b = bit(v);
    const r = ROW_OF[i];
    const c = COL_OF[i];
    const bx = BOX_OF[i];
    // Widerspruch bereits in den Vorgaben
    if (rows[r] & b || cols[c] & b || boxes[bx] & b) return null;
    rows[r] |= b;
    cols[c] |= b;
    boxes[bx] |= b;
  }
  return { rows, cols, boxes };
}

function candidatesAt(masks: Masks, index: number): number {
  return (
    ALL_CANDIDATES & ~(masks.rows[ROW_OF[index]] | masks.cols[COL_OF[index]] | masks.boxes[BOX_OF[index]])
  );
}

/**
 * Zählt Lösungen bis maximal `limit`. Mit `limit = 2` lässt sich Eindeutigkeit
 * prüfen, ohne den gesamten Suchbaum abzulaufen.
 */
export function countSolutions(grid: Grid, limit = 2, rng?: Rng): number {
  const masks = buildMasks(grid);
  if (!masks) return 0;
  const work = Uint8Array.from(grid);
  let found = 0;

  const search = (): boolean => {
    let best = -1;
    let bestMask = 0;
    let bestCount = 10;
    for (let i = 0; i < 81; i++) {
      if (work[i] !== 0) continue;
      const mask = candidatesAt(masks, i);
      const count = popcount(mask);
      if (count === 0) return false; // Sackgasse
      if (count < bestCount) {
        best = i;
        bestMask = mask;
        bestCount = count;
        if (count === 1) break;
      }
    }
    if (best === -1) {
      found++;
      return found >= limit; // Abbruch, sobald das Limit erreicht ist
    }

    const r = ROW_OF[best];
    const c = COL_OF[best];
    const bx = BOX_OF[best];
    let digits: number[] = [];
    for (let m = bestMask; m; m &= m - 1) digits.push(lowestDigit(m & -m));
    if (rng) digits = shuffle(digits, rng);

    for (const d of digits) {
      const b = bit(d);
      work[best] = d;
      masks.rows[r] |= b;
      masks.cols[c] |= b;
      masks.boxes[bx] |= b;

      const stop = search();

      work[best] = 0;
      masks.rows[r] &= ~b;
      masks.cols[c] &= ~b;
      masks.boxes[bx] &= ~b;

      if (stop) return true;
    }
    return false;
  };

  search();
  return found;
}

/** Liefert die erste gefundene Lösung oder null. Mit `rng` wird zufällig gesucht. */
export function solve(grid: Grid, rng?: Rng): Grid | null {
  const masks = buildMasks(grid);
  if (!masks) return null;
  const work = Uint8Array.from(grid);

  const search = (): boolean => {
    let best = -1;
    let bestMask = 0;
    let bestCount = 10;
    for (let i = 0; i < 81; i++) {
      if (work[i] !== 0) continue;
      const mask = candidatesAt(masks, i);
      const count = popcount(mask);
      if (count === 0) return false;
      if (count < bestCount) {
        best = i;
        bestMask = mask;
        bestCount = count;
        if (count === 1) break;
      }
    }
    if (best === -1) return true;

    const r = ROW_OF[best];
    const c = COL_OF[best];
    const bx = BOX_OF[best];
    let digits: number[] = [];
    for (let m = bestMask; m; m &= m - 1) digits.push(lowestDigit(m & -m));
    if (rng) digits = shuffle(digits, rng);

    for (const d of digits) {
      const b = bit(d);
      work[best] = d;
      masks.rows[r] |= b;
      masks.cols[c] |= b;
      masks.boxes[bx] |= b;
      if (search()) return true;
      work[best] = 0;
      masks.rows[r] &= ~b;
      masks.cols[c] &= ~b;
      masks.boxes[bx] &= ~b;
    }
    return false;
  };

  return search() ? work : null;
}

export function hasUniqueSolution(grid: Grid): boolean {
  return countSolutions(grid, 2) === 1;
}

/** Prüft, ob das Gitter widerspruchsfrei ist (leere Felder erlaubt). */
export function isConsistent(grid: Grid): boolean {
  return buildMasks(grid) !== null;
}

/** Vollständig und widerspruchsfrei gefüllt? */
export function isSolved(grid: Grid): boolean {
  for (let i = 0; i < 81; i++) if (grid[i] === 0) return false;
  return isConsistent(grid);
}

/** Indizes, die gegen eine Regel verstoßen (Duplikat in Zeile/Spalte/Block). */
export function conflictsOf(grid: Grid, index: number): boolean {
  const v = grid[index];
  if (v === 0) return false;
  for (const p of PEERS[index]) if (grid[p] === v) return true;
  return false;
}
